'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface PushAuditRow {
  id: string
  created_at: string
  triggered_by: string
  triggered_by_user_id: string | null
  target_monthly_cents: number
  push_succeeded: boolean
  error: string | null
  objects_updated: unknown
}

interface PushStatus {
  push_enabled: boolean
  paused_reason: string | null
}

export function PushHistoryPanel({
  open,
  onOpenChange,
  connectionId,
  platformName,
  onDisabled,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  connectionId: string
  platformName: string
  onDisabled?: () => void | Promise<void>
}) {
  const [pushes, setPushes] = useState<PushAuditRow[]>([])
  const [status, setStatus] = useState<PushStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false)
  const [disabling, setDisabling] = useState(false)

  const load = useCallback(async (cancelled?: { current: boolean }) => {
    setLoading(true)
    try {
      const [historyRes, statusRes] = await Promise.all([
        fetch(`/api/connectors/connections/${connectionId}/push-history?limit=20`),
        fetch(`/api/connectors/connections/${connectionId}/push`),
      ])
      const historyData = await historyRes.json().catch(() => ({})) as { pushes?: PushAuditRow[]; error?: string }
      const statusData = await statusRes.json().catch(() => ({})) as PushStatus & { error?: string }
      if (!historyRes.ok) throw new Error(historyData.error || 'Failed to load push history')
      if (!statusRes.ok) throw new Error(statusData.error || 'Failed to load push status')
      if (cancelled?.current) return
      setPushes(historyData.pushes ?? [])
      setStatus(statusData)
    } catch (err) {
      if (!cancelled?.current) {
        toast.error(err instanceof Error ? err.message : 'Failed to load push history')
      }
    } finally {
      if (!cancelled?.current) setLoading(false)
    }
  }, [connectionId])

  useEffect(() => {
    if (!open) return
    const cancelled = { current: false }
    void load(cancelled)
    return () => { cancelled.current = true }
  }, [load, open])

  const disablePush = async () => {
    setDisabling(true)
    try {
      const res = await fetch(`/api/connectors/connections/${connectionId}/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'disable' }),
      })
      const data = await res.json().catch(() => ({})) as { error?: string }
      if (!res.ok || data.error) {
        toast.error(data.error || 'Failed to disable push')
        return
      }
      setConfirmDisableOpen(false)
      await onDisabled?.()
      void load()
    } finally {
      setDisabling(false)
    }
  }

  const stateLabel = status?.push_enabled
    ? status.paused_reason
      ? 'Paused'
      : 'Live'
    : 'Off'

  return (
    <>
      <SlideOutPanel open={open} onOpenChange={onOpenChange} direction="right" className="md:max-w-md">
        <div className="sticky top-0 z-10 border-b border-atrium-border bg-atrium-content px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-atrium-black">{platformName} push history</h2>
              <p className="mt-0.5 text-xs text-atrium-muted">{stateLabel}</p>
            </div>
            <button
              type="button"
              onClick={() => setConfirmDisableOpen(true)}
              disabled={!status?.push_enabled}
              className="rounded-md border border-atrium-border bg-white px-3 py-1.5 text-xs font-medium text-atrium-black transition-colors hover:bg-atrium-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Disable push
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {loading ? (
            <div className="py-8 text-sm text-atrium-muted">Loading push history...</div>
          ) : pushes.length === 0 ? (
            <div className="rounded-lg border border-dashed border-atrium-border bg-atrium-paper px-4 py-6 text-sm text-atrium-muted">
              No pushes yet. The first slider change with push enabled will appear here.
            </div>
          ) : (
            <div className="space-y-3">
              {pushes.map((push) => (
                <div key={push.id} className="rounded-lg border border-atrium-border bg-atrium-paper px-3 py-3">
                  <div className="text-sm text-atrium-black">
                    {relativeTime(push.created_at)} - {triggerLabel(push.triggered_by)} - {formatCents(push.target_monthly_cents)}
                  </div>
                  <div className={push.push_succeeded ? 'mt-1 text-xs text-green-700' : 'mt-1 text-xs text-red-600'}>
                    {push.push_succeeded ? '✓ pushed' : `✗ ${push.error || 'push failed'}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SlideOutPanel>

      <Dialog open={confirmDisableOpen} onOpenChange={setConfirmDisableOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Disable push?</DialogTitle>
            <DialogDescription>
              Atrium will stop PATCHing Meta budgets for this connection. Budgets already pushed
              to Meta stay there until you change them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmDisableOpen(false)}
              className="rounded-lg border border-atrium-border bg-white px-4 py-2 text-sm text-atrium-black transition-colors hover:bg-atrium-content"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={disablePush}
              disabled={disabling}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {disabling ? 'Disabling...' : 'Disable push'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function triggerLabel(triggeredBy: string): string {
  switch (triggeredBy) {
    case 'user_slider': return 'you (slider)'
    case 'chat_tool': return 'you (chat)'
    case 'smart_spend': return 'Smart Spend'
    case 'reconciliation': return 'reconciliation'
    case 'ceiling_raise': return 'ceiling raised'
    case 'opt_in': return 'push enabled'
    case 'opt_out': return 'push disabled'
    default: return triggeredBy.replaceAll('_', ' ')
  }
}

function relativeTime(value: string): string {
  const timestamp = new Date(value).getTime()
  if (Number.isNaN(timestamp)) return value
  const seconds = Math.max(1, Math.round((Date.now() - timestamp) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
